// Decides what happens to an offline-queued mutation whose replay did not succeed. The queue
// holds staff writes — check-in, session edits, player-assessment — so dropping one unheard
// loses work the user believes landed.

// Replaying one of these could repeat a real-world effect the server cannot yet dedupe (an
// invite email), so a failure retires it rather than risking a second send. Everything else
// converges on replay: check-in is a $addToSet, the edits are last-write-wins on a field set.
const NON_IDEMPOTENT_OPERATIONS = new Set(['SendInvite']);

// The codes the server raises for a refusal it decided deliberately, so the replay would be
// refused identically. Mirrors ACCESS_DENIAL_CODES minus UNAUTHENTICATED, which a token blip
// can produce transiently. Anything else — INTERNAL_SERVER_ERROR, a network drop — is retried.
const PERMANENT_REFUSAL_CODES = new Set(['BAD_USER_INPUT', 'FORBIDDEN']);

type GraphQLErrorLike = { extensions?: { code?: string } };

export type ReplayOutcome = 'retry' | 'retire';

// Both shapes in one reader: an Apollo rejection carries graphQLErrors, a resolved 200 that the
// server refused carries errors. Either means the server answered rather than the network failing.
export const replayErrorCodes = (outcome: unknown): string[] => {
  const source = outcome as { graphQLErrors?: GraphQLErrorLike[]; errors?: GraphQLErrorLike[] } | null | undefined;
  return [...(source?.graphQLErrors ?? []), ...(source?.errors ?? [])]
    .map((err) => err?.extensions?.code)
    .filter((code): code is string => Boolean(code));
};

export const hasGraphQLError = (outcome: unknown): boolean => {
  const source = outcome as { graphQLErrors?: unknown[]; errors?: unknown[] } | null | undefined;
  return Boolean(source?.graphQLErrors?.length) || Boolean(source?.errors?.length);
};

// How many hydrate passes a replay may fail before it stops being retried. Matches the score
// path's cap: a replay runs on an offline→online transition, so five is days of reconnections.
export const MAX_REPLAY_ATTEMPTS = 5;

// `retry` keeps the query queued for the next hydrate pass; `retire` drops it and the caller
// tells the user. Defaults to retry: keeping a write costs a redundant request, dropping it
// costs the work itself — but a write that has failed identically MAX_REPLAY_ATTEMPTS times is
// not going to land, and retrying it forever hides that from everyone.
export const decideReplayOutcome = (
  { name, outcome, attempts = 0 }: { name: string; outcome: unknown; attempts?: number },
): ReplayOutcome =>
  (NON_IDEMPOTENT_OPERATIONS.has(name)
    || replayErrorCodes(outcome).some((code) => PERMANENT_REFUSAL_CODES.has(code))
    || attempts >= MAX_REPLAY_ATTEMPTS
    ? 'retire'
    : 'retry');
